
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useKanban } from './useKanban';
import { useKanbanBulkActions } from './useKanbanBulkActions';
import { useBulkSelection } from '../../../hooks/useBulkSelection';
import { useLeads } from '../../../contexts/leads/LeadsContext';
import { useTasks } from '../../../contexts/tasks/TasksContext';
import { QuickFiltersState } from '../../../components/shared/filters/QuickFilters';
import { Lead } from '../../../types';
import { duplicateLeadAsCopy } from '../utils/leadFormHelpers';

export const useKanbanPage = () => {
    // 1. Core Kanban Logic (Aggregated)
    const kanban = useKanban();

    // 2. Extra Contexts needed for Bulk Actions
    const { funnelStages, saveLead } = useLeads();
    const { tasks } = useTasks();

    const [currentPage, setCurrentPage] = useState(1);
    const [groupByOrigin, setGroupByOrigin] = useState(false);
    const pageSize = 10;

    const paginatedLeads = useMemo(() => {
        if (kanban.viewMode !== 'list') return kanban.filteredLeads;
        const list = groupByOrigin
            ? [...kanban.filteredLeads].sort((a, b) =>
                (a.origin || '').localeCompare(b.origin || '', 'pt-BR', { sensitivity: 'base' }),
              )
            : kanban.filteredLeads;
        const start = (currentPage - 1) * pageSize;
        return list.slice(start, start + pageSize);
    }, [kanban.viewMode, kanban.filteredLeads, currentPage, pageSize, groupByOrigin]);

    useEffect(() => {
        setCurrentPage(1);
    }, [kanban.filters, kanban.searchTerm, kanban.selectedFunnelId, groupByOrigin]);

    const selectionScope =
        kanban.viewMode === 'list' ? paginatedLeads : kanban.filteredLeads;

    // 3. Bulk Selection Logic
    const selection = useBulkSelection(kanban.filteredLeads, selectionScope);

    // 4. Bulk Actions Logic
    const bulkActions = useKanbanBulkActions({
        getSelectedItems: selection.getSelectedItems,
        clearSelection: selection.clearSelection,
        funnelStages,
        users: kanban.users
    });

    // --- UI Handlers ---

    const handleQuickFilterChange = useCallback((quickFilters: QuickFiltersState) => {
        kanban.setFilters(prev => ({
            ...prev,
            noAgent: quickFilters.noAgent,
            noTasks: quickFilters.noTasks,
            overdueTasks: quickFilters.overdueTasks,
            statusWon: quickFilters.statusWon,
            statusLost: quickFilters.statusLost
        }));
    }, [kanban.setFilters]);

    const handleStageFilterChange = useCallback((stageId: string) => {
        kanban.setFilters(prev => ({
            ...prev,
            stage: stageId
        }));
    }, [kanban.setFilters]);

    const handleDuplicateLead = useCallback(
        (lead: Lead) => {
            void duplicateLeadAsCopy(
                lead,
                kanban.contacts,
                funnelStages,
                kanban.selectedFunnelId,
                saveLead
            );
        },
        [kanban.contacts, kanban.selectedFunnelId, funnelStages, saveLead]
    );

    // Aggregate everything into a single return object
    return {
        // Kanban Base
        ...kanban,

        // Tasks Context (exposed for Toolbar/Filters if needed)
        tasks,

        // List pagination (visualização em lista)
        currentPage,
        setCurrentPage,
        pageSize,
        paginatedLeads,
        groupByOrigin,
        setGroupByOrigin,

        // Selection
        selection, /* { selectedIds, isAllSelected, toggleSelection... } */

        // Bulk Actions
        bulk: bulkActions, /* { bulkActions, isBulkDeleteModalOpen, handleBulkDelete... } */

        // Page Handlers
        handleQuickFilterChange,
        handleStageFilterChange,
        handleDuplicateLead
    };
};
